import type { Editor, Path, Point } from 'slate';

import type {
  AlineElement,
  AlineText,
  BookmarkEndElement,
  BookmarkStartElement,
  BreakElement,
  BreakVariant,
  CannedFieldElement,
  CheckboxElement,
  CheckboxOptionFieldElement,
  DateFieldElement,
  DocumentElement,
  DrawingElement,
  DropdownFieldElement,
  FooterElement,
  FootersElement,
  HeaderElement,
  HeadersElement,
  InlineInstructionFieldElement,
  LinkElement,
  ListElement,
  ListLevelDefinition,
  NumberFieldElement,
  ParagraphBorders,
  ParagraphElement,
  RadioButtonFieldElement,
  RenderModelDynamicBlock,
  RenderModelDynamicInline,
  RenderModelEmptySplitNodeText,
  RenderModelTable,
  RenderModelTableCell,
  RenderModelTableRow,
  RenderModelText,
  SignatureFieldElement,
  SuggestedEdit,
  SuggestionMarkerEndElement,
  SuggestionMarkerStartElement,
  TabElement,
  TextFieldElement,
  TOCElement,
} from '../../internal';

export type RMDocProps = Omit<RenderModelDocument, 'children'>;

export interface RenderModelDocument extends Omit<DocumentElement, 'children'> {
  children: RenderModelPage[];
  footers: RenderModelFooters;
  headers: RenderModelHeaders;
  relativePath: Path;
}

export interface RenderModelHeaders extends Omit<HeadersElement, 'children'> {
  children: RenderModelHeader[];
  relativePath: Path;
}
export interface RenderModelHeadersElement extends Omit<HeadersElement, 'children'> {
  children: RenderModelHeader[];
}
export interface RenderModelHeader extends Omit<HeaderElement, 'children'> {
  children: RenderModelContentBlock[];
  heightPx: number;
  relativePath: Path;
}

export interface RenderModelFooters extends Omit<FootersElement, 'children'> {
  children: RenderModelFooter[];
  relativePath: Path;
}
export interface RenderModelFootersElement extends Omit<FootersElement, 'children'> {
  children: RenderModelFooter[];
}
export interface RenderModelFooter extends Omit<FooterElement, 'children'> {
  children: RenderModelContentBlock[];
  heightPx: number;
  relativePath: Path;
}

export type RenderModelContentBlock =
  | RenderModelParagraph
  | RenderModelTable
  | RenderModelDynamicBlock
  | RenderModelTOC;

export type PageType = 'page';
export const pageType: PageType = 'page';

export interface RenderModelPage {
  children: RenderModelContentBlock[];
  maxContentHeightPx: number;
  pageNum: number;
  suggestedEdits: SuggestedEdit[];
  type: PageType;
}

export interface RenderModelList extends Omit<ListElement, 'children'> {
  children: RenderModelParagraph[];
}

export interface ListMarker extends AlineText {
  listLevelDefinition: ListLevelDefinition;
}

export interface RenderModelParagraph extends Omit<ParagraphElement, 'children'> {
  /**
   * Bottom border layout inset (thickness + space) included in heightPx when
   * this fragment draws a bottom border.
   */
  borderLayoutBottomPx: number;
  /**
   * Top border layout inset (thickness + space) included in heightPx when this
   * fragment draws a top border.
   */
  borderLayoutTopPx: number;
  borderMergesWithNext: boolean;
  borderMergesWithPrev: boolean;
  /** Paragraph borders resolved against adjacent paragraphs during render-model construction. */
  bordersToDraw: ParagraphBorders | undefined;
  children: RenderModelLine[];
  heightPx: number;
  key: string;
  listMarker: ListMarker | null;
  prevParaSpacingAfterPx: number | null;
  relativePath: Path;
  /** True when `w:contextualSpacing` applies against the neighbour on that side. */
  spacingAfterSuppressed: boolean;
  spacingBeforeSuppressed: boolean;
  splitIndex: number;
  widthPx: number;
  paragraphHasBreaks?: boolean;
}

export type Line = 'line';
export const lineType: Line = 'line';

export interface RenderModelLine {
  /**
   * The vertical position of the baseline relative to the top of the line.
   */
  baselinePx: number;
  children: RenderModelLineChild[];
  heightPx: number;
  /**
   * Indicates whether it's the first line of the paragraph (used to render first line indent).
   */
  isFirstLine: boolean;
  /**
   * Indicates whether it's the last line in the paragraph (useful to render the cursor on the last
   * line).
   */
  isLastLine: boolean;
  leadingSpacesWidthPx: number;
  /**
   * The maximum typographical descent of any child of the line.
   */
  maxDescentPx: number;
  /**
   * The end point of the *content model* node that corresponds to the end of the specific render
   * model line *relative to its parent paragraph*.
   */
  relativeEndPoint: Point;
  /**
   * The start point of the *content model* node that corresponds to the start of the specific
   * render model line *relative to its parent paragraph*.
   */
  relativeStartPoint: Point;
  spaceWidthPx: number | `default`;
  suggestedEdits: SuggestedEdit[];
  type: Line;
  widthPx: number;
  contentBreak?: { variant: BreakVariant };
}

export interface RenderModelInlineBase {
  key: string;
  relativePath: Path;
}

/**
 * I don't know why, but extracting children up to RenderModelInlineBase causes type inference inside React components to revert to Node.
 * So, we put the children on each specific inline interface.
 */
export interface RenderModelLink extends LinkElement, RenderModelInlineBase {
  children: (RenderModelTab | RenderModelText)[];
}

export interface RenderModelTextField extends TextFieldElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelNumberField extends NumberFieldElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelDateField extends DateFieldElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelDropdownField extends DropdownFieldElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelRadioButtonField
  extends RadioButtonFieldElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelCheckboxOption
  extends CheckboxOptionFieldElement, RenderModelInlineBase {
  children: (RenderModelCheckbox | RenderModelTab | RenderModelText)[];
}

export interface RenderModelSignatureField extends SignatureFieldElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelDrawing extends DrawingElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelBookmarkStart extends BookmarkStartElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelBookmarkEnd extends BookmarkEndElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelSuggestionMarkerStart
  extends SuggestionMarkerStartElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelSuggestionMarkerEnd
  extends SuggestionMarkerEndElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelTab extends TabElement, RenderModelInlineBase {
  children: RenderModelText[];
  tabWidthPx: number;
}
export type RenderModelTabProps = Omit<RenderModelTab, 'children'>;

export interface RenderModelCheckbox extends CheckboxElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelInlineInstructionField
  extends InlineInstructionFieldElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelTOC extends Omit<TOCElement, 'children'> {
  children: RenderModelContentBlock[];
  heightPx: number;
  key: string;
  relativePath: Path;
}

export type RMNonSignatureField =
  | RenderModelTextField
  | RenderModelNumberField
  | RenderModelDateField
  | RenderModelDropdownField
  | RenderModelCheckboxOption
  | RenderModelRadioButtonField;

export type RenderModelInputField = RMNonSignatureField | RenderModelSignatureField;

export interface RenderModelCannedField extends CannedFieldElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export interface RenderModelBreak extends BreakElement, RenderModelInlineBase {
  children: RenderModelText[];
}

export type RenderModelBlock =
  | RenderModelDocument
  | RenderModelDynamicBlock
  | RenderModelFooter
  | RenderModelFootersElement
  | RenderModelHeader
  | RenderModelHeadersElement
  | RenderModelList
  | RenderModelPage
  | RenderModelParagraph
  | RenderModelTable
  | RenderModelTableRow
  | RenderModelTableCell
  | RenderModelTOC;

export type RenderModelInline =
  | RenderModelLink
  | RenderModelInputField
  | RenderModelCannedField
  | RenderModelBreak
  | RenderModelDynamicInline
  | RenderModelDrawing
  | RenderModelTab
  | RenderModelBookmarkStart
  | RenderModelBookmarkEnd
  | RenderModelCheckbox
  | RenderModelCheckboxOption
  | RenderModelSuggestionMarkerStart
  | RenderModelSuggestionMarkerEnd
  | RenderModelInlineInstructionField;

export type RenderModelElement = RenderModelBlock | RenderModelLine | RenderModelInline;
export type RenderModelLineChild = RenderModelInline | RenderModelText;
export type RenderModelAncestor = RenderModelElement;
export type RenderModelDescendant =
  | RenderModelElement
  | RenderModelText
  | RenderModelEmptySplitNodeText;
export type RenderModelNode = Editor | RenderModelDescendant;
export type RenderModelNodeWithRelPath =
  | RenderModelDocument
  | RenderModelDynamicBlock
  | RenderModelFooter
  | RenderModelHeader
  | RenderModelParagraph
  | RenderModelTable
  | RenderModelTableRow
  | RenderModelTableCell
  | RenderModelTOC
  | RenderModelInline
  | RenderModelText;

/**
 * Make sure all element types are represented in the render model.
 *
 * Note that elements can appear in the render model even if they're not listed in
 * RenderModelElement. This is why it's important to have an exhaustive list, so that switch
 * statements can ensure that all cases are handled to prevent runtime errors.
 *
 * If there are any types that genuinely shouldn't be handled (and you've verified that they never
 * appear in the render model), add them to OmittedType.
 */
() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  function assertSubtype<T, U extends T>() {}
  type OmittedType = 'fake';
  assertSubtype<RenderModelElement['type'] | OmittedType, AlineElement['type']>();
  assertSubtype<AlineElement['type'], OmittedType>(); // Check for outdated entries in OmittedType
};
